import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { getAnimal } from "@/lib/animals";

const ThemeContext = createContext(null);

function hexToHsl(hex) {
  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  if (max === min) { h = s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const [mode, setMode] = useState(() => localStorage.getItem("kh_mode") || "light");
  const [preview, setPreview] = useState(null); // temporary brand hex for live preview

  const toggleMode = useCallback((m) => {
    const next = m || (mode === "dark" ? "light" : "dark");
    setMode(next);
    localStorage.setItem("kh_mode", next);
  }, [mode]);

  useEffect(() => {
    const root = document.documentElement;
    if (mode === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
  }, [mode]);

  useEffect(() => {
    const root = document.documentElement;
    let brand = "#b76e79"; // default elegant rose
    if (user && user.role === "sysadmin") {
      brand = "#16a34a"; // forest green for sysadmin
      root.setAttribute("data-sysadmin", "true");
    } else if (user && user.animal) {
      brand = getAnimal(user.animal).c;
      root.removeAttribute("data-sysadmin");
    }
    if (preview) brand = preview;
    root.style.setProperty("--brand", brand);
    const hsl = hexToHsl(brand);
    root.style.setProperty("--primary", hsl);
    root.style.setProperty("--ring", hsl);
    root.style.setProperty("--primary-foreground", "0 0% 100%");
  }, [user, mode, preview]);

  return (
    <ThemeContext.Provider value={{ mode, toggleMode, setPreview }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
